import {OpenInNew} from '@mui/icons-material';
import {Box, Button, Typography, type SxProps, type Theme} from '@mui/material';
import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';

import {useExtensionConnection} from '../../hooks/extension/ExtensionProvider';

interface OpenExtensionPopupButtonProps {
  variant?: 'contained' | 'outlined' | 'text';
  color?: 'primary' | 'error' | 'warning' | 'inherit';
  fullWidth?: boolean;
  size?: 'small' | 'medium' | 'large';
  sx?: SxProps<Theme>;
}

// Bouton « Ouvrir la popup de l'extension » + fallback honnête.
// Quand l'extension répond `opened=false` (API absente, user-gesture refusé, erreur), on affiche
// l'instruction de repli (clic manuel sur l'icône) sans jamais prétendre que la popup s'est ouverte.
export const OpenExtensionPopupButton = ({
  variant = 'contained',
  color = 'primary',
  fullWidth = true,
  size = 'medium',
  sx,
}: OpenExtensionPopupButtonProps) => {
  const {t} = useTranslation();
  const {openExtensionPopup} = useExtensionConnection();
  const [loading, setLoading] = useState(false);
  const [showFallback, setShowFallback] = useState(false);

  const handleClick = async () => {
    setLoading(true);
    try {
      const {opened} = await openExtensionPopup();
      // On n'affiche le repli que si l'ouverture a échoué. On ne le masque jamais une fois affiché :
      // si l'utilisateur reclique et que ça échoue encore, l'instruction reste pertinente.
      if (!opened) setShowFallback(true);
    } catch {
      setShowFallback(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{display: 'flex', flexDirection: 'column', gap: 1, ...sx}}>
      <Button
        variant={variant}
        color={color}
        fullWidth={fullWidth}
        size={size}
        disabled={loading}
        onClick={handleClick}
        startIcon={<OpenInNew />}
        sx={{fontWeight: 600}}
      >
        {t('extension.open_popup.button', "Ouvrir la popup de l'extension")}
      </Button>
      {showFallback && (
        <Typography variant="caption" color="text.secondary">
          {t(
            'extension.open_popup.fallback',
            "Cliquez sur l'icône PerfectPost en haut à droite de votre navigateur pour vous reconnecter.",
          )}
        </Typography>
      )}
    </Box>
  );
};
